import { useEffect, useRef, useState } from 'react';
import './manage-game-news.styles.scss';
import { useLocation } from 'react-router-dom';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import GameNewsList from '@/components/SHARED/game-news/game-news-list.component';
import MessageModal from './message-modal.component';

const ManageGameNews = ({
  gameData,
  gameMessages,
  role
}) => {

  const canManage = role === 'owner' || role === 'manager';
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageToEdit, setMessageToEdit] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const approveRef = useRef();
  const location = useLocation();

  useEffect(() => {
    if (!gameData?.gameName) return;
    changeDocumentTitle(`Game News | Manage ${gameData.gameName}`);
    const pageTitleEl = document.getElementById('page-title');
    if (pageTitleEl) {
      pageTitleEl.innerText = 'Game News';
    }
  }, [gameData?.gameName])

  function openEditor(message) {
    setMessageToEdit(message);
    setShowMessageModal(true);
  }

  function closeEditor() {
    setShowMessageModal(false);
    setMessageToEdit(null);
  }

  // ts is left alone so switching an item on or off does not mark it edited.
  // Switching it on restamps ts_activated, which pops it up for every player
  // again even if they hid the popup within the last 6 hours.
  async function toggleActive(message, active) {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! You cannot change messages in Preview Mode.');
      return;
    }
    const data = { ...message, 'active': active };
    if (active) {
      data.ts_activated = new Date().getTime();
    }
    const res = await UseCloudFunction(
      'saveMessage',
      {
        'gameId': gameData.gameId,
        'data': data
      }
    )
    if (res.error) {
      setAlertMessage(`Something went wrong. Error: ${res.error}`);
    }
  }

  function confirmDelete(message) {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! You cannot delete messages in Preview Mode.');
      return;
    }
    setConfirmMessage(`Are you sure you want to delete "${message.title || 'this news item'}"? This cannot be undone.`);
    approveRef.current = async () => {
      const res = await UseCloudFunction(
        'deleteMessage',
        {
          'gameId': gameData.gameId,
          'messageId': message.id
        }
      )
      if (res.error) {
        setAlertMessage(`Something went wrong. Error: ${res.error}`);
      }
    }
  }

  return (
    <div className='manage-game-news-container'>
      <div className='manage-game-news-content g-card'>
        <div className='manage-game-news-head'>
          <div className='card-title'>Game News</div>
          {
            (canManage) &&
            <button
              className='g-button primary small-btn'
              onClick={() => openEditor(null)}>
              Post a News Item
            </button>
          }
        </div>
        <div className='meta'>
          Active items pop up together on each player&apos;s home page, newest first. When a player closes the popup, it comes back 6 hours later as long as an item is still active. To show an item to everyone again right away, switch it off and back on. Players can read every item, active or not, from their Game News page.
        </div>
        <hr />
        <GameNewsList
          gameMessages={gameMessages}
          onEdit={canManage ? openEditor : null}
          onDelete={canManage ? confirmDelete : null}
          onToggleActive={canManage ? toggleActive : null} />
      </div>
      <MessageModal
        gameData={gameData}
        show={showMessageModal}
        cancel={closeEditor}
        message={messageToEdit} />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current}
        buttonText='Delete' />
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
    </div>
  )
}

export default ManageGameNews
